import env from '@beam-australia/react-env';
import axios from 'axios';
import upperCase from 'lodash/upperCase';
import queryString from 'query-string';
import {
  INSTIL_API_VERSION_HEADER,
  INSTIL_AUTH_HEADER,
  INSTIL_CLIENT_SCOPE_HEADER,
} from '@/lib/constants/api';
import {
  AUTH_USER_ID_TOKEN,
  AUTH_X_INSTIL,
  CORRELATION_ID,
  OWNER_ORG_ID_PUB,
  USER_ID_PUB,
  USER_NAME_PUB,
} from '@/lib/constants/localStorageKeys';
import translate from '@/lib/intl/translate';
import { camelCaseKeys, snakeCaseKeys } from '@/utils/transformKeys';

const handleData = (response) => {
  if (!response) return null;

  const { data } = response;

  return data;
};
const handleErrors = (error) => {
  let formattedError;
  let returnError = error;

  if (error.response && error.response.data) {
    returnError = error.response.data;
  }

  try {
    formattedError = error.toJSON();
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Failed to format JSON', error);
    formattedError = translate('GENERIC_ERROR');
  }

  // eslint-disable-next-line no-console
  console.warn(formattedError);

  return returnError;
};
const formatAuthBearerHeader = (accessToken) => `Bearer ${accessToken}`;

// Builds a base-64 encoded string from current user id and org id to provide the API with context
const buildClientScopeHeader = (userId, orgId, csId, userName) =>
  btoa(
    JSON.stringify({
      user_id: userId,
      org_id: orgId,
      cs_id: csId,
      user_name: userName,
    }),
  );

/**
 * @template RequestData
 * @template ResponseData
 * @param {RequestData} request parameters
 * @param {ResponseData} response
 * @return {Promise<ResponseData>}
 *
 * @example const req = createApiRequest<Request, Response>(...)
 */
const createApiRequest = ({
  config = undefined,
  data = undefined,
  method = 'post',
  postProcessMethod = camelCaseKeys,
  preProcessMethod = snakeCaseKeys,
  path,
  apiVersion = 2,
  useAuth = true,
  throwErrors = false,
} = {}) => {
  if (!method || !path) {
    throw new Error('Invalid URL or Method');
  }

  const requestConfig = { ...config };

  const authToken = localStorage.getItem(AUTH_USER_ID_TOKEN);
  const instilXAuthToken = localStorage.getItem(AUTH_X_INSTIL);
  const headers = {};

  // Default content type header
  // TODO if API requests are integrated that do not required application/json add as a parameter
  headers['Content-Type'] = 'application/json';

  if (useAuth && authToken) {
    headers.Authorization = formatAuthBearerHeader(authToken);

    const csId = localStorage.getItem(CORRELATION_ID);
    const userId = localStorage.getItem(USER_ID_PUB);
    const orgId = localStorage.getItem(OWNER_ORG_ID_PUB);
    const userName = localStorage.getItem(USER_NAME_PUB);

    // Append the client scope header
    if (csId && userId && orgId && userName) {
      headers[INSTIL_CLIENT_SCOPE_HEADER.toLowerCase()] =
        buildClientScopeHeader(userId, orgId, csId, userName);
    }
  }

  if (apiVersion) {
    headers[INSTIL_API_VERSION_HEADER] = `${apiVersion}`;
  }

  // This header is required on all API requests for user informational purposes
  if (instilXAuthToken) {
    headers[INSTIL_AUTH_HEADER.toLowerCase()] = instilXAuthToken;
  }

  if (typeof requestConfig === 'object') {
    requestConfig.headers = headers;
  }

  const isProd = process.env.NODE_ENV === 'production';
  const apiPath = isProd
    ? `${env('API_URL')}${env('API_PREFIX')}/${path}`
    : `${env('API_PREFIX')}/${path}`;
  const requestParams = [apiPath, requestConfig];

  if (['POST', 'PATCH', 'PUT'].includes(upperCase(method))) {
    requestParams.splice(
      1,
      0,
      preProcessMethod && data ? preProcessMethod(data) : data,
    );
  }
  if (requestParams[1]?.params) {
    requestParams[1].paramsSerializer = (params) => {
      return queryString.stringify(params, {
        arrayFormat: 'comma',
        skipNull: true,
      });
    };
  }

  return axios[method](...requestParams)
    .then((result) => postProcessMethod(handleData(result)))
    .catch((error) => {
      handleErrors(error);

      // Error API data response if exists
      if (throwErrors) {
        throw camelCaseKeys(error?.response?.data);
      }
    });
};

// TODO(jon): These two utilities below are deprecated in favor of the
// TypeScript enabled ones at utils/swr/fetcher and
// utils/swr/fetcherWithDataKey
/**
 * @deprecated
 */
const fetcher = (path, options = {}) =>
  createApiRequest({
    path,
    method: 'get',
    throwErrors: true,
    ...options,
  })
    .then((res) => res?.data)
    .catch((error) => {
      if (!error) {
        throw new Error(`fetch error for ${path}`);
      } else {
        const apiError = new Error(error.error_enum);
        apiError.info = error;

        throw apiError;
      }
    });

/**
 * @deprecated
 */
const fetcherWithDataKey =
  (key) =>
  async (path, options = {}) => {
    const data = await fetcher(path, options);
    return data?.[key];
  };

export {
  createApiRequest,
  fetcher,
  fetcherWithDataKey,
  handleData,
  handleErrors,
  buildClientScopeHeader,
};
